import React, { useEffect, useState, useRef } from 'react';
import useIsInEditor from '../../../hooks/useIsInEditor';
import useSiteSettings from '../../../hooks/useSiteSettings';
import { resolveRichText } from '../../../utils/richText';
import { getCookie, setCookie } from '../../../utils/cookie-helpers';
import * as styles from './CookiePopup.module.scss';
import CookiePreferencesModal from '../../internal/cookiePreferencesModal/CookiePreferencesModal';
import useGlobalPrivacyControl from '../../../hooks/useGlobalPrivacyControl';
import { FormattedMessage } from 'react-intl';

const COOKIE_CONSENT_NAME = 'cookie-consent';
const COOKIE_PREFERENCES_NAME = 'cookie-preferences';

const updateGTMConsent = (preferences) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_update',
      adStorageUpdate: preferences.targeting ? 'granted' : 'denied',
      analyticsStorageUpdate: preferences.performance ? 'granted' : 'denied',
      functionalityStorageUpdate: 'granted',
      personalizationStorageUpdate: preferences.targeting ? 'granted' : 'denied',
      securityStorageUpdate: 'granted',
      adUserDataUpdate: preferences.targeting ? 'granted' : 'denied',
      adPersonalizationUpdate: preferences.targeting ? 'granted' : 'denied',
    });
  }
};

const CookiePopup = ({ locale }) => {
  const isInEditor = useIsInEditor();
  const settings = useSiteSettings(locale);
  const text = resolveRichText(settings.cookie_message);
  const messageRef = useRef(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    performance: false,
    targeting: false,
  });
  useGlobalPrivacyControl(setCookiePreferences);

  useEffect(() => {
    updateGTMConsent(cookiePreferences);
  }, [cookiePreferences]);

  const handleAcceptAll = () => {
    const newPreferences = { necessary: true, performance: true, targeting: true };
    setCookiePreferences(newPreferences);
    setCookie(COOKIE_CONSENT_NAME, 'true');
    setCookie(COOKIE_PREFERENCES_NAME, JSON.stringify(newPreferences));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const newPreferences = { necessary: true, performance: false, targeting: false };
    setCookiePreferences(newPreferences);
    setCookie(COOKIE_CONSENT_NAME, 'true');
    setCookie(COOKIE_PREFERENCES_NAME, JSON.stringify(newPreferences));
    setShowBanner(false);
  };

  const handleSavePreferences = (newPreferences) => {
    setCookiePreferences(newPreferences);
    setCookie(COOKIE_CONSENT_NAME, 'true');
    setCookie(COOKIE_PREFERENCES_NAME, JSON.stringify(newPreferences));
    setShowPreferencesModal(false);
  };

  useEffect(() => {
    if (!isInEditor) {
      const consentCookie = getCookie(COOKIE_CONSENT_NAME);
      const preferencesCookie = getCookie(COOKIE_PREFERENCES_NAME);

      if (!consentCookie) {
        setShowBanner(true);
      } else if (preferencesCookie) {
        const savedPreferences = JSON.parse(preferencesCookie);
        setCookiePreferences(savedPreferences);
      }
    }
  }, [isInEditor]);

  useEffect(() => {
    const handleOpenPreferences = (e) => {
      e.preventDefault();
      setShowPreferencesModal(true);
      // Remove the hash from the URL without triggering a page reload
      history.pushState('', document.title, window.location.pathname + window.location.search);
    };

    const setupCookieSettingsLinks = () => {
      const cookieSettingsLinks = document.querySelectorAll('a[href="#cookies-settings"]');
      cookieSettingsLinks.forEach((link) => {
        link.setAttribute('role', 'button');
        link.setAttribute('tabindex', '0');
        link.setAttribute('aria-label', 'Open cookie preferences');

        // Remove the href attribute and add a data attribute
        link.removeAttribute('href');
        link.setAttribute('data-action', 'open-cookie-preferences');

        link.addEventListener('click', handleOpenPreferences);
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOpenPreferences(e);
          }
        });
      });
    };

    // Check hash on initial load
    if (window.location.hash === '#cookies-settings') {
      setShowPreferencesModal(true);
      // Remove the hash from the URL without triggering a page reload
      history.pushState('', document.title, window.location.pathname + window.location.search);
    }

    // Set up the links
    setupCookieSettingsLinks();

    // Set up a MutationObserver to handle dynamically added links
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          setupCookieSettingsLinks();
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup function
    return () => {
      observer.disconnect();
      const cookieSettingsLinks = document.querySelectorAll('[data-action="open-cookie-preferences"]');
      cookieSettingsLinks.forEach((link) => {
        link.removeEventListener('click', handleOpenPreferences);
        link.removeEventListener('keydown', handleOpenPreferences);
      });
    };
  }, []);

  return (
    <>
      {showBanner && (
        <div className={styles.wrapper}>
          <div className={styles.innerWrapper}>
            <div ref={messageRef} className={styles.message} dangerouslySetInnerHTML={text}></div>

            <div className={styles.buttonWrapper}>
              <button className='btn transparent-white white-hover' onClick={() => setShowPreferencesModal(true)}>
                <FormattedMessage id='component.cookie_banner.manage_preferences' />
              </button>
              <button className='btn transparent-white white-hover' onClick={handleRejectAll}>
                <FormattedMessage id='component.cookie_banner.reject_all' />
              </button>
              <button className='btn transparent-white white-hover' onClick={handleAcceptAll}>
                <FormattedMessage id='component.cookie_banner.allow_all' />
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreferencesModal && (
        <CookiePreferencesModal
          preferences={cookiePreferences}
          onSave={handleSavePreferences}
          onClose={() => setShowPreferencesModal(false)}
          open={showPreferencesModal}
          settings={settings}
        />
      )}
    </>
  );
};

export default CookiePopup;
